import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import ProjectStatusBadge from './ProjectStatusBadge';
import ProjectProgressBar from './ProjectProgressBar';
import { projectService } from '../../../services/projectService';

const ProjectDetailModal = ({ project, onClose, onEdit, onArchived }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [archiving, setArchiving] = useState(false);

  if (!project) return null;

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    })?.format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const parts = String(dateString)?.split('T')?.[0]?.split('-');
    if (parts?.length !== 3) return dateString;
    const [year, month, day] = parts;
    return `${month}/${day}/${year}`;
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'Info' },
    { id: 'phases', label: 'Phases', icon: 'GitBranch' },
    { id: 'financial', label: 'Financial', icon: 'DollarSign' },
    { id: 'resources', label: 'Resources', icon: 'Users' }
  ];

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm">
      <div className="bg-card rounded-xl shadow-2xl border border-border w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-start justify-between p-4 md:p-6 border-b border-border">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              {project?.convertedFromBid && (
                <div className="flex items-center gap-2 px-3 py-1 bg-accent/10 rounded-full">
                  <Icon name="FileText" size={14} className="text-accent" />
                  <span className="text-xs font-medium text-accent">From Bid</span>
                </div>
              )}
              <span className="text-sm font-medium text-muted-foreground data-text">
                {project?.projectNumber}
              </span>
            </div>
            <h2 className="text-xl md:text-2xl font-semibold text-foreground mb-2">
              {project?.projectName}
            </h2>
            <div className="flex flex-wrap items-center gap-3">
              <ProjectStatusBadge status={project?.status} />
              <span className="text-sm text-muted-foreground">{project?.customerName}</span>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <Icon name="X" size={24} />
          </Button>
        </div>

        {/* Tabs */}
        <div className="border-b border-border overflow-x-auto">
          <div className="flex px-4 md:px-6">
            {tabs?.map((tab) => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === tab?.id
                    ? 'border-accent text-accent' :'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon name={tab?.icon} size={16} />
                <span>{tab?.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 md:p-6">
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-base md:text-lg font-semibold text-foreground mb-4">Project Progress</h3>
                <ProjectProgressBar percentage={project?.completionPercentage} size="lg" />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-muted/50 rounded-lg p-4">
                  <p className="text-sm text-muted-foreground mb-1">Start Date</p>
                  <p className="text-base font-semibold text-foreground data-text">
                    {formatDate(project?.startDate)}
                  </p>
                </div>
                <div className="bg-muted/50 rounded-lg p-4">
                  <p className="text-sm text-muted-foreground mb-1">End Date</p>
                  <p className="text-base font-semibold text-foreground data-text">
                    {formatDate(project?.endDate)}
                  </p>
                </div>
                <div className="bg-muted/50 rounded-lg p-4">
                  <p className="text-sm text-muted-foreground mb-1">Total Budget</p>
                  <p className="text-base font-semibold text-foreground data-text">
                    {formatCurrency(project?.totalBudget)}
                  </p>
                </div>
                <div className="bg-muted/50 rounded-lg p-4">
                  <p className="text-sm text-muted-foreground mb-1">Location</p>
                  <p className="text-base font-semibold text-foreground">{project?.location}</p>
                </div>
                <div className="bg-muted/50 rounded-lg p-4">
                  <p className="text-sm text-muted-foreground mb-1">Assigned PM</p>
                  <p className="text-base font-semibold text-foreground">
                    {project?.assignedPm?.fullName || project?.assignedPm?.email || <span className="text-muted-foreground font-normal">Not assigned</span>}
                  </p>
                </div>
              </div>

              <div>
                <h3 className="text-base md:text-lg font-semibold text-foreground mb-2">Description</h3>
                <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                  {project?.description}
                </p>
              </div>

              {project?.convertedFromBid && (
                <div className="bg-accent/10 rounded-lg p-4 border border-accent/20">
                  <div className="flex items-center gap-2 mb-3">
                    <Icon name="FileText" size={20} className="text-accent" />
                    <h3 className="text-base font-semibold text-foreground">Original Bid Information</h3>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Bid Number</p>
                      <p className="text-sm font-medium text-foreground data-text">{project?.bidNumber}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Conversion Date</p>
                      <p className="text-sm font-medium text-foreground data-text">
                        {formatDate(project?.conversionDate)}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'phases' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base md:text-lg font-semibold text-foreground">Project Phases</h3>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('/project-phases', { state: { projectId: project?.id } })}
                  iconName="GitBranch"
                  iconPosition="left"
                >
                  Manage Phases
                </Button>
              </div>
              {project?.phases?.map((phase) => (
                <div key={phase?.id} className="bg-muted/50 rounded-lg p-4 space-y-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className="text-base font-semibold text-foreground mb-1">{phase?.name}</h4>
                      <p className="text-sm text-muted-foreground">{phase?.description}</p>
                    </div>
                    <ProjectStatusBadge status={phase?.status} />
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                    <div>
                      <p className="text-muted-foreground mb-1">Start Date</p>
                      <p className="font-medium text-foreground data-text">{formatDate(phase?.startDate)}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground mb-1">End Date</p>
                      <p className="font-medium text-foreground data-text">{formatDate(phase?.endDate)}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground mb-1">Deliverables</p>
                      <p className="font-medium text-foreground">{phase?.deliverableCount}</p>
                    </div>
                  </div>
                  <ProjectProgressBar percentage={phase?.progress} size="sm" />
                </div>
              ))}
            </div>
          )}

          {activeTab === 'financial' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-muted/50 rounded-lg p-4">
                  <p className="text-sm text-muted-foreground mb-2">Total Budget</p>
                  <p className="text-xl md:text-2xl font-bold text-foreground data-text">
                    {formatCurrency(project?.totalBudget)}
                  </p>
                </div>
                <div className="bg-muted/50 rounded-lg p-4">
                  <p className="text-sm text-muted-foreground mb-2">Actual Cost</p>
                  <p className="text-xl md:text-2xl font-bold text-foreground data-text">
                    {formatCurrency(project?.actualCost)}
                  </p>
                </div>
                <div className="bg-muted/50 rounded-lg p-4">
                  <p className="text-sm text-muted-foreground mb-2">Variance</p>
                  <p className={`text-xl md:text-2xl font-bold data-text ${
                    project?.variance >= 0 ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {project?.variance >= 0 ? '+' : ''}{formatCurrency(project?.variance)}
                  </p>
                </div>
              </div>

              <div>
                <h3 className="text-base md:text-lg font-semibold text-foreground mb-4">Cost Breakdown</h3>
                <div className="space-y-3">
                  {project?.costBreakdown?.map((item, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                      <span className="text-sm font-medium text-foreground">{item?.category}</span>
                      <span className="text-sm font-semibold text-foreground data-text">
                        {formatCurrency(item?.amount)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'resources' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base md:text-lg font-semibold text-foreground">Assigned Resources</h3>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('/resource-management')}
                  iconName="Users"
                  iconPosition="left"
                >
                  Manage Resources
                </Button>
              </div>

              {project?.resources?.map((resource) => (
                <div key={resource?.id} className="bg-muted/50 rounded-lg p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h4 className="text-base font-semibold text-foreground mb-1">{resource?.name}</h4>
                      <p className="text-sm text-muted-foreground">{resource?.type}</p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      resource?.status === 'Available' ?'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' :'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
                    }`}>
                      {resource?.status}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <p className="text-muted-foreground mb-1">Assigned Phase</p>
                      <p className="font-medium text-foreground">{resource?.assignedPhase}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground mb-1">Utilization</p>
                      <p className="font-medium text-foreground">{resource?.utilization}%</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 p-4 md:p-6 border-t border-border">
          <div className="flex gap-2 w-full sm:w-auto">
            <Button
              variant="outline"
              onClick={onClose}
              className="w-full sm:w-auto"
            >
              Close
            </Button>
            <Button
              variant="outline"
              onClick={async () => {
                setArchiving(true);
                try {
                  const { error } = await projectService?.archive(project?.id, project?.status);
                  if (!error) {
                    onArchived && onArchived(project);
                    onClose();
                  }
                } catch (err) {
                  console.error('Archive error:', err?.message);
                } finally {
                  setArchiving(false);
                }
              }}
              disabled={archiving}
              iconName="Archive"
              iconPosition="left"
              iconSize={14}
              className="text-muted-foreground hover:text-foreground"
            >
              {archiving ? 'Archiving...' : 'Archive'}
            </Button>
          </div>
          <div className="flex gap-2 w-full sm:w-auto">
            <Button
              variant="outline"
              onClick={() => navigate('/project-phases', { state: { projectId: project?.id } })}
              iconName="GitBranch"
              iconPosition="left"
              className="flex-1 sm:flex-none"
            >
              Manage Phases
            </Button>
            <Button
              variant="default"
              iconName="Edit"
              iconPosition="left"
              className="flex-1 sm:flex-none"
              onClick={() => onEdit && onEdit(project)}
            >
              Edit Project
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectDetailModal;